import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import type { ReadClientPlanItem } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";
import { useRefetchOnOverlayClose } from "./useRefetchOnOverlayClose";

export type PlanState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; items: ReadClientPlanItem[] };

// Same fetch shape and refetch triggers as useHomeSurface: home becoming the
// active tab again, and an overlay closing. The plan block's text lives here
// rather than on the home payload, so it must refresh on the same beats.
export function usePlan(): PlanState {
  const api = useApi();
  const [state, setState] = useState<PlanState>({ status: "loading" });
  const cancelledRef = useRef(false);
  const { pathname } = useLocation();

  const fetchPlan = useCallback(() => {
    api.plan
      .get()
      .then(({ items }) => {
        if (!cancelledRef.current) {
          setState({ status: "success", items });
        }
      })
      .catch((err: unknown) => {
        if (!cancelledRef.current) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load plan" });
        }
      });
  }, [api]);

  useEffect(() => {
    cancelledRef.current = false;
    fetchPlan();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchPlan]);

  const isActive = pathname === "/";
  const wasActiveRef = useRef(isActive);
  useEffect(() => {
    if (isActive && !wasActiveRef.current) fetchPlan();
    wasActiveRef.current = isActive;
  }, [isActive, fetchPlan]);

  useRefetchOnOverlayClose(fetchPlan);

  return state;
}
